import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Users as UsersIcon, Trash2, ShieldCheck } from 'lucide-react';

import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import MultiSelectDropdown from '../../components/ui/MultiSelectDropdown';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

/* ── Menu items that can be permission-controlled ─────────── */
const MENU_ITEMS = [
  { key: 'dashboard',        label: 'Dashboard',        hasActions: false },
  { key: 'ai_chats',         label: 'AI Chats',         hasActions: false },
  { key: 'chats',            label: 'Chats',            hasActions: false },
  { key: 'tickets',          label: 'Tickets',          hasActions: false },
  { key: 'customers',        label: 'Customers',        hasActions: true  },
  { key: 'subscriptions',    label: 'Subscriptions',    hasActions: false },
  { key: 'payment_history',  label: 'Payment History',  hasActions: false },
  { key: 'partners',         label: 'Partners',         hasActions: true  },
  { key: 'withdrawals',      label: 'Withdrawals',      hasActions: false },
  { key: 'settings',         label: 'Settings',         hasActions: false },
  { key: 'ai_config',        label: 'AI Config',        hasActions: false },
  { key: 'ai_health',        label: 'AI Health',        hasActions: false },
  { key: 'reports',          label: 'Reports',          hasActions: false },
  { key: 'users',            label: 'Users Management', hasActions: true  },
  { key: 'groups',           label: 'Groups',           hasActions: true  },
];

const defaultPermission = (hasActions) => ({
  visible: false,
  ...(hasActions ? { add: false, edit: false, delete: false } : {}),
});

const buildDefault = () => {
  const p = {};
  MENU_ITEMS.forEach(m => { p[m.key] = defaultPermission(m.hasActions); });
  return p;
};

const userTypeLabel = (t) => (Number(t) === 1 ? 'Admin' : Number(t) === 2 ? 'Manager' : 'Agent');

const StatusBadge = ({ status }) => {
  const active = Number(status) === 1;
  return (
    <span className={active ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const ModalShell = ({ open, title, children, onClose, wide }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`panel w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} mx-4 p-0 app-modal-shell`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="app-modal-body p-6">{children}</div>
      </div>
    </div>
  );
};

/* ── Permissions Modal ────────────────────────────────────── */
const PermissionsModal = ({ open, onClose, userId, userName }) => {
  const [perms, setPerms] = useState(buildDefault());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    companyAdminCustomersAPI.getUserPermissions(userId)
      .then(res => {
        const saved = res.data?.permissions || {};
        const merged = buildDefault();
        MENU_ITEMS.forEach(m => {
          if (saved[m.key]) {
            merged[m.key] = { ...merged[m.key], ...saved[m.key] };
          }
        });
        setPerms(merged);
      })
      .catch(() => toast.error('Failed to load permissions'))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const toggle = (key, field, value) => {
    setPerms(p => {
      const updated = { ...p, [key]: { ...p[key], [field]: value } };
      // If hiding, also uncheck all actions
      if (field === 'visible' && !value) {
        updated[key] = { ...updated[key], add: false, edit: false, delete: false };
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.updateUserPermissions(userId, perms);
      toast.success('Permissions saved');
      onClose();
    } catch {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title={`Permissions — ${userName || ''}`} wide>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* Header row */}
          <div className="grid grid-cols-[1fr_80px_70px_70px_70px] gap-2 px-3 py-2 bg-muted rounded-lg mb-1 text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <span>Menu Item</span>
            <span className="text-center">Visible</span>
            <span className="text-center">Add</span>
            <span className="text-center">Edit</span>
            <span className="text-center">Delete</span>
          </div>

          <div className="space-y-1">
            {MENU_ITEMS.map(m => {
              const p = perms[m.key] || {};
              return (
                <div
                  key={m.key}
                  className={`grid grid-cols-[1fr_80px_70px_70px_70px] gap-2 items-center px-3 py-2.5 rounded-lg border transition-colors ${p.visible ? 'border-blue-100 bg-blue-50/40' : 'border-transparent hover:bg-muted'}`}
                >
                  <span className="text-sm font-medium text-foreground">{m.label}</span>

                  {/* Visible toggle */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => toggle(m.key, 'visible', !p.visible)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${p.visible ? 'bg-blue-500' : 'bg-slate-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-card shadow transition-transform ${p.visible ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Add */}
                  <div className="flex justify-center">
                    {m.hasActions ? (
                      <input
                        type="checkbox"
                        checked={!!p.add}
                        disabled={!p.visible}
                        onChange={(e) => toggle(m.key, 'add', e.target.checked)}
                        className="w-4 h-4 rounded accent-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </div>

                  {/* Edit */}
                  <div className="flex justify-center">
                    {m.hasActions ? (
                      <input
                        type="checkbox"
                        checked={!!p.edit}
                        disabled={!p.visible}
                        onChange={(e) => toggle(m.key, 'edit', e.target.checked)}
                        className="w-4 h-4 rounded accent-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </div>

                  {/* Delete */}
                  <div className="flex justify-center">
                    {m.hasActions ? (
                      <input
                        type="checkbox"
                        checked={!!p.delete}
                        disabled={!p.visible}
                        onChange={(e) => toggle(m.key, 'delete', e.target.checked)}
                        className="w-4 h-4 rounded accent-red-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Permissions
            </Button>
          </div>
        </>
      )}
    </ModalShell>
  );
};

/* ── Groups Modal ─────────────────────────────────────────── */
const GroupsModal = ({ open, onClose, groups }) => (
  <ModalShell open={open} onClose={onClose} title="Assigned Groups">
    {groups?.length ? (
      <div className="max-h-[420px] overflow-auto border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Group</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => (
              <TableRow key={g.id}>
                <TableCell className="font-medium text-foreground">{g.name}</TableCell>
                <TableCell className={Number(g.status) === 1 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {Number(g.status) === 1 ? 'Active' : 'Inactive'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    ) : (
      <div className="text-sm text-muted-foreground">No groups assigned.</div>
    )}
    <div className="flex justify-end mt-4">
      <Button variant="outline" onClick={onClose}>Close</Button>
    </div>
  </ModalShell>
);

/* ── User Form Modal ──────────────────────────────────────── */
const UserForm = ({ mode, open, onClose, groups, initial, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', status: 1, user_type: 3, group_ids: [] });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      email: initial?.email || '',
      password: '',
      status: Number(initial?.status ?? 1),
      user_type: Number(initial?.user_type ?? 3),
      group_ids: initial?.group_ids || [],
    });
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (mode === 'add' && !form.password) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      if (mode === 'add') {
        await companyAdminCustomersAPI.createCompanyUser(form);
        toast.success('User created');
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await companyAdminCustomersAPI.updateCompanyUser(initial.user_id, payload);
        toast.success('User updated');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title={mode === 'add' ? 'Add User' : 'Edit User'}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-foreground">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label className="text-foreground">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Label className="text-foreground">Password {mode === 'edit' ? '(leave blank to keep)' : ''}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <div>
          <Label className="text-foreground">Status</Label>
          <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>
        <div>
          <Label className="text-foreground">User Type</Label>
          <select className="form-select" value={form.user_type} onChange={(e) => setForm({ ...form, user_type: Number(e.target.value) })}>
            <option value={1}>Admin</option>
            <option value={2}>Manager</option>
            <option value={3}>Agent</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-foreground">Groups</Label>
          <MultiSelectDropdown
            options={groups || []}
            value={form.group_ids || []}
            onChange={(selectedIds) => setForm({ ...form, group_ids: selectedIds })}
            placeholder="Select groups..."
            labelKey="name"
            valueKey="id"
          />
          <div className="text-xs text-muted-foreground mt-2">
            {mode === 'add' ? 'Only active groups are shown.' : 'All groups are shown.'}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save
        </Button>
      </div>
    </ModalShell>
  );
};

/* ── Main Page ────────────────────────────────────────────── */
const UsersPage = () => {
  const { user, canAction } = useCompanyAdminAuth();
  const isManager = Number(user?.user_type) === 2;
  const canAdd    = canAction('users', 'add');
  const canEdit   = canAction('users', 'edit');
  const canDelete = canAction('users', 'delete');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [groupsActive, setGroupsActive] = useState([]);
  const [groupsAll, setGroupsAll] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [groupsModalData, setGroupsModalData] = useState([]);

  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);

  const groupsByIdAll = useMemo(() => {
    const map = new Map();
    (groupsAll || []).forEach((g) => map.set(g.id, g));
    return map;
  }, [groupsAll]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, gActiveRes, gAllRes] = await Promise.all([
        companyAdminCustomersAPI.listCompanyUsers({ page: 1, limit: 200 }),
        companyAdminCustomersAPI.listGroups({ include_inactive: false }),
        companyAdminCustomersAPI.listGroups({ include_inactive: true }),
      ]);
      setUsers(uRes.data.users || []);
      setGroupsActive(gActiveRes.data.groups || []);
      setGroupsAll(gAllRes.data.groups || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const confirmDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    try {
      await companyAdminCustomersAPI.deleteCompanyUser(deleteUser.user_id);
      toast.success('User deleted');
      setDeleteOpen(false);
      setDeleteUser(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const openGroupsModal = (u) => {
    const ids = u.group_ids || [];
    setGroupsModalData(ids.map((id) => groupsByIdAll.get(id)).filter(Boolean));
    setGroupsModalOpen(true);
  };

  return (
    <CompanyAdminLayout>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <UsersIcon className="w-5 h-5" /> Users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage Admins, Managers and Agents.</p>
        </div>
        {canAdd && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        )}
      </div>

      <div className="panel border border-border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>User Type</TableHead>
                  <TableHead>Groups</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium text-foreground">{u.name || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email || '-'}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-muted-foreground">{userTypeLabel(u.user_type)}</TableCell>
                    <TableCell>
                      <button onClick={() => openGroupsModal(u)} className="text-primary text-sm font-semibold hover:underline">
                        {Number(u.groups_count ?? (u.group_ids || []).length)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      {(canAdd || canEdit || canDelete) && (
                        <div className="flex items-center justify-end gap-1">
                          {/* Permissions button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-blue-50 text-blue-500 hover:text-blue-600"
                            onClick={() => { setPermUser(u); setPermOpen(true); }}
                            title="Manage Permissions"
                            data-testid={`user-permissions-${u.user_id}`}
                          >
                            <ShieldCheck className="w-4 h-4" />
                          </Button>
                          {canEdit && <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-muted"
                            onClick={() => { setEditUser(u); setEditOpen(true); }}
                            aria-label="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>}
                          {canDelete && <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-muted"
                            onClick={() => { setDeleteUser(u); setDeleteOpen(true); }}
                            aria-label="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Delete modal */}
      <ModalShell open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete User">
        <div className="text-sm text-muted-foreground">
          Are you sure you want to delete <span className="font-semibold text-foreground">{deleteUser?.email || deleteUser?.name}</span>?
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </div>
      </ModalShell>

      <GroupsModal open={groupsModalOpen} onClose={() => setGroupsModalOpen(false)} groups={groupsModalData} />

      <PermissionsModal
        open={permOpen}
        onClose={() => { setPermOpen(false); setPermUser(null); }}
        userId={permUser?.user_id}
        userName={permUser?.name}
      />

      {canAdd && (
        <UserForm mode="add" open={addOpen} onClose={() => setAddOpen(false)} groups={groupsActive} onSaved={fetchAll} />
      )}
      {canEdit && (
        <UserForm mode="edit" open={editOpen} onClose={() => setEditOpen(false)} groups={groupsAll} initial={editUser} onSaved={fetchAll} />
      )}
    </CompanyAdminLayout>
  );
};

export default UsersPage;
